/**
 * Sidebar — collapsible left rail (Desktop: 160px voll / 64px Icon-Rail,
 * localStorage-persistiert) / 280px Mobile-Drawer.
 * Logo („Assets"-Wordmark), Nav-Gruppen, Collapse-Toggle (Kanten-Button).
 *
 * Eingeführt in Slice-1 Redesign (2026-05-08).
 * Redesign S2 Phase 1 (2026-10-10, `tasks/design-handoff-redesign-s2.md`):
 * HELL (`bg-white` + Border rechts) statt dunklem Grün; aktiver Punkt =
 * `bg-brand-green-100 text-brand-green-700 font-semibold`. Gruppen: ohne Label
 * (Start · Library · Favoriten) · „Organisieren" (Collections · Rezepte · Brand
 * Center) · „Teilen" (Share-Links · Händler-Links); unten Insights ·
 * Papierkorb · Einstellungen · Speicher · Account. Upload ist raus (gelber CTA
 * in der Topbar), die Glocke ebenso. Collapse/Drawer-Verhalten unverändert.
 * Slice-3 (2026-05-09) erweitert um Mobile-Drawer-CSS (position-fixed
 * <md, position-static ≥md, transform-driven), first-nav-link-ref für
 * focus-restore, auto-close-on-link-click (Codex CO1 Path B), Logout-
 * Button entfernt (kanonische Position ist jetzt im AvatarMenu in Topbar).
 *
 * - Cmd+K-Hint im Search-Item rendert NUR auf `/` (Library-Route) — Shortcut
 *   ist heute page-local in LibraryPage (Slice-4-Backlog: Layout-Level).
 * - Bibliothek-Section: 6 Kategorie-Items (Sprint C6) sind aktive `<Link>`s
 *   auf `/library?tag=<slugs>` — Tag-Filter aus `CATEGORY_MAPPING`.
 * - stats-Fetch (`/api/assets/stats`, OHNE Filter-Params) speist den Papierkorb-
 *   Counter-Pill. Die frühere Speicher-Anzeige wurde 2026-07-19 entfernt (der
 *   Fetch bleibt für den Counter). Eigener AbortController + alive-flag,
 *   unabhängig vom Library-Header-Fetch.
 * - Slice 3: KEIN Logout-Button mehr im Footer. Logout-Action lebt im
 *   AvatarMenu in der Topbar (kanonische Position, Slack/GitHub/Notion-
 *   Convention).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { toolsSettingsUrl } from '../lib/tools-login';
import type { AssetStats } from '../../shared/asset-stats-types';
import { api } from '../lib/api';
import { TRASH_CHANGED, ASSETS_CHANGED } from '../lib/events';
import { I, type IconKey } from '../lib/icons';
import { useSidebarState } from '../lib/layout-context';
import { formatBytes } from '../lib/format-bytes';
import { Pill } from './ui/Pill';
import { SidebarAccount } from './SidebarAccount';
// Logo lebt in public/ (stabile, ungehashte URL) — die SSR-Share-Seiten
// (lib/share-html.ts) referenzieren dieselbe Datei und können keine
// Vite-gehashten Asset-Imports auflösen (2026-07-08).
const logoUrl = '/greenforce-logo.png';

type Props = {
  /**
   * Aktueller User. `displayName` speist die Avatar-Initialen im
   * `SidebarAccount` (2026-07-19 aus der Topbar hierher gezogen). `isAdmin`
   * steuert seit dem P1c-Cutover das Ziel des „Einstellungen"-Items: Admins
   * bleiben DAM-intern (Vokabular), Member gehen direkt in die tools-Settings.
   * Das Item selbst ist weiterhin für alle sichtbar.
   */
  user: { displayName: string; role: string; isAdmin: boolean };
  /**
   * Logout-Handler fürs Avatar-Menü im `SidebarAccount`. Optional NUR damit die
   * isolierten Sidebar-Unit-Tests (die `SidebarAccount` wegmocken) ihn weglassen
   * können — der reale Layout-Aufruf liefert ihn immer.
   */
  onLogout?: () => void;
};

// Onboarding-Tour: stabile data-tour-Anker an den Shell-Nav-Items (First-Run-
// Orientierungs-Tour, src/client/lib/onboarding-tour.ts). Andere Items: kein Anker.
const NAV_TOUR_ANCHORS: Record<string, string | undefined> = {
  library: 'nav-library',
  recipes: 'nav-recipes',
  collections: 'nav-collections',
  settings: 'nav-settings',
};

type NavItem = {
  id: string;
  label: string;
  icon: IconKey;
  /** Ziel-Route des Link-Items. */
  href: string;
  /**
   * Ziel liegt auf einer FREMDEN Origin (P1c: die tools-Settings) → rendert ein
   * natives `<a>` statt eines wouter-`<Link>`. wouter würde die absolute URL als
   * App-internen Pfad interpretieren und die Navigation abfangen.
   */
  external?: boolean;
  /** Match-fn fürs Active-Highlight. Default: location === href. */
  match?: (location: string) => boolean;
  /**
   * Optionaler rechtsbündiger Slot im Link-Body (zwischen Label und Ende).
   * Z.B. Counter-Pill für Trash-Item.
   */
  right?: ReactNode;
};

// Nav-Gruppen (S2): navMain (Start · Library · Favoriten) · navOrganize ·
// navShare · navSecondary (unterer Block). Alle in der Component via useMemo
// gebaut; navSecondary hängt an `stats` (Papierkorb-Counter-Pill rechts).
// Dashboard ist die Default-Landing (`/` → /dashboard) und steht zuoberst
// (IA-Audit 2026-06-09).

type StatsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; stats: AssetStats };

export function Sidebar({ user, onLogout }: Props) {
  const [location] = useLocation();
  const { sidebarOpen, setSidebarOpen, firstNavLinkRef, collapsed, toggleCollapsed } =
    useSidebarState();
  // Defensiv: außerhalb des Providers / in Tests kann der Wert fehlen → als
  // ausgeklappt (false) behandeln. Alle Collapse-Styles sind `md:`-gated, der
  // Mobile-Drawer bleibt unberührt.
  const isCollapsed = !!collapsed;

  // Primary-Nav = reine Ziele (keine Filter). Die offene Kategorie-Taxonomie
  // lebt seit #196 im rechten Filter-Panel (ProductCategoryTree), nicht mehr in
  // der Sidebar — Leitprinzip „Nav = wo bin ich, Filter = was sehe ich".
  // S2: die frühere flache Primary-Liste ist in drei Gruppen geteilt (navMain ·
  // navOrganize · navShare). Item-IDs/Matches unverändert.
  const navMain = useMemo<NavItem[]>(
    () => [
      // Dashboard ist die Default-Landing (`/` → /dashboard) — gehört zuoberst,
      // nicht in die gedämpfte „Mehr"-Gruppe (IA-Audit 2026-06-09: Inversion,
      // die Startseite war de-priorisiert).
      {
        id: 'dashboard',
        // S2: Label „Start" (Route bleibt /dashboard).
        label: 'Start',
        icon: 'home',
        href: '/dashboard',
        match: (loc) => loc === '/dashboard',
      },
      {
        id: 'library',
        label: 'Library',
        icon: 'image',
        href: '/library',
        // Aktiv auf /library + auf Asset-Detailseiten /assets/:id (konzeptionell
        // „unter" Library, kanonischer Asset-Home). Kein Kategorie-Sonderfall
        // mehr — die Kategorie-Liste ist aus der Sidebar raus.
        match: (loc) => loc === '/library' || loc.startsWith('/assets/'),
      },
      // Favoriten (Roadmap 3a Phase 2) — direkt unter Library (Asset-Sicht).
      {
        id: 'favorites',
        label: 'Favoriten',
        icon: 'star',
        href: '/favorites',
        match: (loc) => loc === '/favorites',
      },
    ],
    [],
  );

  // „Organisieren" — S2-Reihenfolge laut Handoff: Collections · Rezepte · Brand
  // Center (ersetzt die Reihenfolge vom 2026-06-11, Canvas freigegeben).
  const navOrganize = useMemo<NavItem[]>(
    () => [
      {
        id: 'collections',
        label: 'Collections',
        icon: 'folder',
        href: '/collections',
        match: (loc) => loc === '/collections' || loc.startsWith('/collections/'),
      },
      {
        id: 'recipes',
        label: 'Rezepte',
        icon: 'grid',
        href: '/recipes',
        match: (loc) => loc === '/recipes' || loc.startsWith('/recipes/'),
      },
      // Brand Center ist eine primäre Content-Destination (Read-only Hub für
      // alle, Admin-Curation unter /brand/admin).
      {
        id: 'brand-center',
        label: 'Brand Center',
        icon: 'sparkle',
        href: '/brand',
        match: (loc) => loc === '/brand' || loc.startsWith('/brand/'),
      },
    ],
    [],
  );

  // „Teilen" — Sharing-Utilities.
  const navShare = useMemo<NavItem[]>(
    () => [
      {
        id: 'shares',
        label: 'Share-Links',
        icon: 'link',
        href: '/shares',
        match: (loc) => loc === '/shares' || loc.startsWith('/shares?'),
      },
      // Händler-Links (Spec Phase 1): permanente öffentliche Bild-URLs für
      // Händler-Systeme — Verwaltungsliste, direkt bei Share-Links (Sharing-
      // Utility-Gruppe). Sichtbar für alle Rollen (Lesen), Aktionen admin-gated.
      {
        id: 'public-links',
        label: 'Händler-Links',
        icon: 'globe',
        href: '/public-links',
        match: (loc) => loc === '/public-links',
      },
    ],
    [],
  );

  // Auto-close drawer on Mobile-link-click (Codex CO1 Path B — same-route
  // case where useLocation()-effect in Layout would NOT fire).
  const closeIfMobile = () => {
    if (!window.matchMedia('(min-width: 768px)').matches) {
      setSidebarOpen(false);
    }
  };

  // Storage-Bar + Trash-Counter: globale Stats (KEIN Filter-Param) —
  // unabhängig vom LibraryHeader-Fetch. Live-Refresh via TRASH_CHANGED-
  // Listener (analog TRASH_CHANGED unten). AbortController-coalescing
  // verhindert out-of-order resolves bei rapid-fire Events.
  const [stats, setStats] = useState<StatsState>({ status: 'loading' });
  const statsCtrlRef = useRef<AbortController | null>(null);
  const refetchStats = useCallback(() => {
    statsCtrlRef.current?.abort();
    const ctrl = new AbortController();
    statsCtrlRef.current = ctrl;
    (async () => {
      try {
        const data = await api<AssetStats>('/api/assets/stats', { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        setStats({ status: 'ok', stats: data });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (ctrl.signal.aborted) return;
        setStats({ status: 'error' });
      }
    })();
  }, []);

  useEffect(() => {
    refetchStats();
    window.addEventListener(TRASH_CHANGED, refetchStats);
    window.addEventListener(ASSETS_CHANGED, refetchStats);
    return () => {
      window.removeEventListener(TRASH_CHANGED, refetchStats);
      window.removeEventListener(ASSETS_CHANGED, refetchStats);
      statsCtrlRef.current?.abort();
    };
  }, [refetchStats]);

  // navSecondary — gedämpfte „Mehr"-Gruppe: gelegentliche/Utility-Ziele
  // (Insights = Analytics, Papierkorb = Recovery). Dashboard + Brand Center
  // sind 2026-06-09 nach Primary hochgezogen (IA-Audit: Dashboard-Inversion +
  // „Mehr"-Grabbelkiste entwirrt). Enthält das einzige NavItem mit dynamischem
  // `right`-Slot (Trash-Counter-Pill) → useMemo reagiert auf stats.
  const navSecondary = useMemo<NavItem[]>(() => {
    const trashCount =
      stats.status === 'ok' ? stats.stats.trashCount : 0;
    return [
      {
        id: 'insights',
        label: 'Insights',
        icon: 'chart',
        href: '/insights',
        match: (loc) => loc === '/insights',
      },
      {
        id: 'trash',
        label: 'Papierkorb',
        icon: 'trash',
        href: '/trash',
        match: (loc) => loc === '/trash',
        // Counter-Pill rendert NUR wenn count > 0 UND stats geladen sind.
        // Kein "0"-Pill als visual-noise, kein "?"-Pill bei error/loading.
        right:
          stats.status === 'ok' && trashCount > 0 ? (
            <Pill
              tone="neutral"
              aria-label={`${trashCount} ${trashCount === 1 ? 'Element' : 'Elemente'} im Papierkorb`}
            >
              {trashCount}
            </Pill>
          ) : undefined,
      },
      // Einstellungen — seit dem P1c-Cutover (Must 12) rollenabhängig:
      //  - Admin: DAM hat mit dem Vokabular noch einen eigenen Settings-Tab →
      //    bleibt App-interne Navigation.
      //  - Member: in DAM ist nichts mehr übrig (Profil + Team liegen auf tools)
      //    → direkter Link dorthin, statt über den DAM-Redirect zu hüpfen. Der
      //    Redirect im SettingsShell bleibt als Netz für Bookmarks/getippte URLs.
      user.isAdmin
        ? {
            id: 'settings',
            label: 'Einstellungen',
            icon: 'settings' as const,
            href: '/settings/vocab',
            match: (loc: string) => loc === '/settings' || loc.startsWith('/settings/'),
          }
        : {
            id: 'settings',
            label: 'Einstellungen',
            icon: 'settings' as const,
            href: toolsSettingsUrl('profile', window.location.hostname),
            external: true,
          },
    ];
  }, [stats, user.isAdmin]);

  // Mobile-Drawer-CSS — position:fixed + translate-driven on <md,
  // position:static on ≥md (Tailwind v4 `md:static md:translate-x-0`).
  // Width: 280px Mobile / 240px Desktop (voll) bzw. 64px (Icon-Rail, collapsed).
  // Desktop animiert die BREITE (statt vorher `md:transition-none`); die
  // reduced-motion-Neutralisierung greift global (styles.css § prefers-reduced-
  // motion → transition-duration 0.01ms). Mobile behält transition-transform.
  const drawerCls = [
    'fixed top-0 left-0 z-50 w-[280px]',
    'transform transition-transform duration-200 ease-out',
    sidebarOpen ? 'translate-x-0' : '-translate-x-full',
    // md:relative (statt static) = Positionierungs-Kontext für den absoluten
    // Kanten-Toggle. Das aktiviert auf Desktop das bisher wirkungslose z-50
    // (static ignoriert z-index) → md:z-30 senkt es wieder UNTER Backdrops/
    // Modals (z-40/z-50), lässt es aber über dem Content (≤ z-20).
    'md:relative md:z-30 md:translate-x-0 md:transition-[width] md:flex-shrink-0',
    // Breite (Desktop): 160px voll (w-40) / 64px Icon-Rail (w-16). 240→160 nach
    // dem Namen-Kürzen „Digital Asset Management"→„Assets" (2026-07-19): die
    // breitesten Labels („Händler-Links"/„Einstellungen") enden bei ~123px, bei
    // 160px bleiben ~25px Puffer zur Highlight-Kante — sauber, kein Umbruch.
    // Praktische Untergrenze wäre ~150px. Mobile-Drawer bleibt 280px (w-[280px]).
    isCollapsed ? 'md:w-16' : 'md:w-40',
    // KEIN overflow am <aside> — das Scrollen liegt im inneren Wrapper. Sonst
    // erzwingt overflow-y:auto implizit overflow-x:auto (CSS-Spec) und clippt
    // den über die rechte Kante ragenden Toggle-Button.
    'flex flex-col bg-white border-r border-ink-200 h-screen',
  ].join(' ');

  return (
    <aside id="app-sidebar" aria-label="Hauptnavigation" className={drawerCls}>
      {/* Innerer Scroll-Wrapper: trägt overflow-y-auto, damit das <aside> selbst
          overflow-visible bleibt (Kanten-Toggle darf über die rechte Kante
          ragen). flex-1 + der flex-1-Spacer unten drücken die Storage-Bar an
          den unteren Rand wie zuvor. */}
      <div className="flex flex-1 flex-col min-h-0 overflow-y-auto">
      {/* Mobile-only Close-Button (Slice-4 Item 1). Erstes tabbable Element
          im Drawer-DOM (vor Logo+Search) — Shift+Tab vom firstNavLinkRef
          walked rückwärts: Search → Logo → X. Auf Desktop versteckt
          (Drawer ist persistent). 44×44 WCAG Touch-Target. */}
      <button
        type="button"
        aria-label="Menü schließen"
        onClick={() => setSidebarOpen(false)}
        className="md:hidden self-end m-2 w-11 h-11 flex items-center justify-center rounded-control text-ink-700 hover:bg-ink-100 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
      >
        <I.close size={20} />
      </button>

      {/* Logo — Name „Assets" als Wordmark. Typografie EINHEITLICH mit der
          Transfer-App-Wortmarke „TRANSFER" (TransferShell.tsx: text-sm font-bold
          uppercase tracking-widest) → beide lesen als Produkt-Familie. Seit S2
          (helle Sidebar) in brand-green-700 wie die Transfer-Wortmarke.
          Collapsed (≥md): zentriert ohne Text. 2026-07-19. */}
      <Link
        href="/library"
        onClick={closeIfMobile}
        title={isCollapsed ? 'Assets' : undefined}
        className={`flex items-center gap-2.5 px-5 py-5 hover:opacity-90 transition ${
          isCollapsed ? 'md:justify-center md:px-0 md:py-4' : ''
        }`}
      >
        <img
          src={logoUrl}
          alt="GREENFORCE"
          className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
        />
        <span
          className={`text-sm font-bold uppercase tracking-widest text-brand-green-700 ${
            isCollapsed ? 'md:hidden' : ''
          }`}
        >
          Assets
        </span>
      </Link>

      {/* Hauptgruppe ohne Label (Start · Library · Favoriten) — erstes Nav-Link
          bekommt den focus-restore-ref (Drawer-Öffnen). Globale Suche lebt in
          der Topbar (<GlobalSearch>, ⌘K app-weit), kein Sidebar-Duplikat. */}
      <NavGroup
        items={navMain}
        location={location}
        className="mt-2"
        firstNavLinkRef={firstNavLinkRef}
        onLinkClick={closeIfMobile}
        collapsed={isCollapsed}
      />

      <NavSection label="Organisieren" collapsed={isCollapsed}>
        <NavGroup
          items={navOrganize}
          location={location}
          onLinkClick={closeIfMobile}
          collapsed={isCollapsed}
        />
      </NavSection>

      <NavSection label="Teilen" collapsed={isCollapsed}>
        <NavGroup
          items={navShare}
          location={location}
          onLinkClick={closeIfMobile}
          collapsed={isCollapsed}
        />
      </NavSection>

      {/* Unterer Block: Insights · Papierkorb (Zähler) · Einstellungen ·
          Speicher. `mt-auto` drückt ihn an den unteren Rand des Scroll-
          Wrappers (direkt über dem Account). */}
      <div className="mt-auto pt-6 pb-2">
        <NavGroup
          items={navSecondary}
          location={location}
          onLinkClick={closeIfMobile}
          collapsed={isCollapsed}
        />
        {stats.status === 'ok' && (
          <div
            data-testid="sidebar-storage"
            className={`mx-3 mt-2 px-2.5 py-1.5 text-xs text-ink-500 ${
              isCollapsed ? 'md:hidden' : ''
            }`}
          >
            Speicher · {formatBytes(stats.stats.totalBytes)}
          </div>
        )}
      </div>

      </div>

      {/* Account (Avatar + Name; S2: Glocke → Topbar). AUSSERHALB des Scroll-Wrappers → als flex-Kind nach dem flex-1-Inner-
          Wrapper an den unteren <aside>-Rand gepinnt. Im overflow-visible
          <aside> (nicht im overflow-y-auto-Inner), damit AvatarMenu (öffnet nach
          oben) + NotificationsPanel (fixed, kippt hoch/rechts) nicht geclippt
          werden. */}
      <SidebarAccount
        user={{ displayName: user.displayName }}
        onLogout={onLogout ?? (() => {})}
        collapsed={isCollapsed}
      />

      {/* Collapse-Toggle — Kanten-Button auf der rechten Sidebar-Naht, UNTEN knapp
          über dem Account-Cluster/Avatar (2026-07-20, User-Wunsch; vorher vertikal
          mittig). `bottom-*` statt `top-1/2`, responsiv: collapsed-Cluster ist
          gestapelt + höher → `bottom-28` statt `bottom-20`, sonst Überlappung.
          NUR Desktop (`hidden md:flex`); der Mobile-Drawer hat oben
          seinen eigenen Close-X. Straddelt die Kante (`translate-x-1/2`, halb im
          Content) → das <aside> ist bewusst overflow-visible (Scroll im inneren
          Wrapper), sonst clippt overflow-x:auto den Button. bg = Sidebar-Grün
          (verschmilzt links) + Border/Shadow (hebt die Content-Hälfte ab).
          Chevron dreht 180° = ausklappen. 2026-07-19. */}
      <button
        type="button"
        onClick={() => toggleCollapsed?.()}
        aria-label={isCollapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
        aria-expanded={!isCollapsed}
        aria-controls="app-sidebar"
        title={isCollapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
        className={`hidden md:flex absolute right-0 z-10 translate-x-1/2 bottom-20 items-center justify-center w-6 h-6 rounded-full bg-white border border-ink-200 text-ink-700 shadow-soft hover:bg-ink-50 hover:text-ink-900 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500`}
      >
        <I.chevL size={13} className={`transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
      </button>
    </aside>
  );
}

/**
 * Gruppen-Überschrift + Inhalt. Collapsed (Icon-Rail, ≥md): statt Label ein
 * dünner Divider. 13px (Typo-Untergrenze), ink-500 = AA auf Weiß.
 */
function NavSection({
  label,
  collapsed,
  children,
}: {
  label: string;
  collapsed: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mt-5">
      <div
        className={`px-5 mb-1 text-xs font-semibold text-ink-500 ${collapsed ? 'md:hidden' : ''}`}
      >
        {label}
      </div>
      {collapsed && (
        <div className="hidden md:block mx-3 mb-2 h-px bg-ink-100" aria-hidden="true" />
      )}
      {children}
    </div>
  );
}

function NavGroup({
  items,
  location,
  className = '',
  firstNavLinkRef,
  onLinkClick,
  collapsed = false,
}: {
  items: NavItem[];
  location: string;
  className?: string;
  /** Ref für das ERSTE enabled-Link in dieser Gruppe (für drawer-focus-on-open). */
  firstNavLinkRef?: React.MutableRefObject<HTMLAnchorElement | null>;
  /** Auto-close drawer on Mobile (path B). No-op on Desktop. */
  onLinkClick?: () => void;
  /** Icon-Rail-Modus (≥md): Labels aus, Icon zentriert, Label als Tooltip. */
  collapsed?: boolean;
}) {
  let firstLinkAssigned = false;
  return (
    <nav className={`flex flex-col gap-px px-3 ${className}`}>
      {items.map((item) => {
        const Icon = I[item.icon];
        const active = item.match ? item.match(location) : location === item.href;
        const refToAssign =
          !firstLinkAssigned && firstNavLinkRef ? firstNavLinkRef : undefined;
        if (refToAssign) firstLinkAssigned = true;
        const linkProps = {
          ref: refToAssign,
          onClick: onLinkClick,
          'data-tour': NAV_TOUR_ANCHORS[item.id],
          'aria-current': active ? ('page' as const) : undefined,
          // Im Icon-Rail ist der Text visuell weg (`md:hidden`, = display:none,
          // damit aus dem A11y-Baum) → aria-label + title tragen den Namen.
          // title dient zugleich als natives Hover-Tooltip. Nur bei collapsed
          // gesetzt, sonst kommt der Name aus dem sichtbaren Label.
          'aria-label': collapsed ? item.label : undefined,
          title: collapsed ? item.label : undefined,
          className: `flex items-center gap-2.5 px-2.5 py-2.5 md:py-2 text-sm rounded-control transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 ${
            collapsed ? 'md:justify-center md:gap-0 md:px-0' : ''
          } ${
            active
              ? 'bg-brand-green-100 text-brand-green-700 font-semibold'
              : 'text-ink-700 hover:bg-ink-50 hover:text-ink-900'
          }`,
        };
        const body = (
          <>
            {/* Icon-Rail: `bold` (nächste Phosphor-Stufe über regular) — im
                Icon-only-Modus tragen die Icons die Bedeutung, etwas mehr Gewicht
                hilft der Lesbarkeit. `weight` ist ein Prop (kein CSS) → gilt bei
                collapsed=true auch im Mobile-Drawer; dort neben Labels minimal
                schwerer, aber unkritisch. */}
            <Icon
              size={18}
              weight={active || collapsed ? 'bold' : undefined}
              className={active ? 'text-brand-green-700' : 'text-ink-500'}
            />
            <span className={`flex-1 ${collapsed ? 'md:hidden' : ''}`}>{item.label}</span>
            {item.right &&
              (collapsed ? (
                <span className="md:hidden">{item.right}</span>
              ) : (
                item.right
              ))}
          </>
        );
        // Fremde Origin (tools-Settings) → natives <a>: wouter würde die absolute
        // URL als App-Pfad behandeln und die Navigation abfangen. Kein
        // `target="_blank"` — der Wechsel zu tools ist ein Orts-, kein
        // Kontextwechsel; ein zweiter Tab wäre hier Ballast.
        return item.external ? (
          <a key={item.id} href={item.href} {...linkProps}>
            {body}
          </a>
        ) : (
          <Link key={item.id} href={item.href} {...linkProps}>
            {body}
          </Link>
        );
      })}
    </nav>
  );
}

